import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";

import { PageHeader } from "../shell/PageHeader";
import { TimeControl } from "../shell/TimeControl";
import { SiteTabBar } from "../components/SiteTabBar";
import {
  useGetV1Assets,
  useGetV1EquipmentId,
  useGetV1Alerts,
  useGetV1Series,
  type Figure,
} from "@solarkey/api-client";
import {
  EmptyState,
  ErrorState,
  HealthBadge,
  Metric,
  Skeleton,
  StateWord,
  type HealthState,
} from "@solarkey/ui";
import { TimeSeriesTile } from "@solarkey/charts";

/* ════════════════════════════════════════════════════════════════════════════
   P7 (list) — Site: Equipment, the register at site scope (wireframe §20).

   The register is the SITE'S OWN REGISTRY, read from GET /v1/assets?site={slug}:
   every row is a real device, and every row's Open button carries that device's
   real registry key, so the drill-down to GET /v1/equipment/{id} resolves.

   It used to be a verbatim transcription of the wireframe's illustrative demo
   rows — invented classes the site does not have, invented counts, invented
   warranty and maintenance dates, and fabricated device ids that 404'd on every
   Open. All of that is gone. Class chips are a real GROUP BY, so a class the site
   does not have is ABSENT (§20 annotation 1), and a column with no source is
   withheld WITH ITS REASON rather than filled in.

   Still withheld here, and why: per-device health, signals n/N and last-seen need
   a site-equipment read that joins the registry to the live store. `equipment.py`
   now has that view-model (`read_site_equipment`) but no endpoint exposes it yet —
   see the note under the table.
   ════════════════════════════════════════════════════════════════════════════ */

/** Plain sentences for the reasons the registry read states. */
const REASON_TEXT: Record<string, string> = {
  NOT_MEASURED_BLOCK_LABEL_NOT_AUTHORED:
    "no block is authored against any device at this site",
  NOT_MEASURED_METER_GROUPS_NOT_AUTHORED:
    "no meter-to-inverter mapping is recorded for this site",
  NOT_MEASURED_NO_TOPOLOGY_EDGES: "device-to-device connectivity is not recorded",
};

/** Columns the register cannot fill from /v1/assets, each with the true reason. */
const WITHHELD_COLUMNS: { column: string; why: string }[] = [
  {
    column: "Health",
    why: "per-device health is judged from the live store; the asset registry read does not carry it",
  },
  {
    column: "Signals n / N",
    why: "signal coverage per device needs the same live-store join",
  },
  {
    column: "Seen",
    why: "per-device last-seen comes from the live store, not the registry",
  },
  {
    column: "Next maintenance",
    why: "no preventive-maintenance schedule is recorded, and the column belongs to a higher tier",
  },
];

interface AssetRow {
  id?: string;
  role_name?: string;
  device_class?: string;
  make?: string | null;
  model?: string | null;
  block_id?: string | null;
  ratings?: Record<string, number | null>;
  meter_group_id?: string | null;
  effective_from?: string | null;
  /* Served additively by assets.py — absent until the contract is regenerated. */
  serial?: string | null;
  monitored?: boolean;
  warranty_to?: string | null;
}

const RATING_UNITS: [string, string][] = [
  ["rated_kw", "kW"],
  ["rated_kva", "kVA"],
  ["rated_kwh", "kWh"],
  ["rated_dc_kwp", "kWp"],
];

function ratingOf(r: AssetRow): string | null {
  const ratings = r.ratings ?? {};
  for (const [key, unit] of RATING_UNITS) {
    const v = ratings[key];
    if (typeof v === "number") return `${v.toLocaleString()} ${unit}`;
  }
  return null;
}

function classLabel(cls: string): string {
  const s = cls.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function SiteEquipment({ site, initialEquipmentId }: {
  site: string;
  initialEquipmentId?: string;
}): ReactNode {
  // The open device is URL state (?equipment=ID), not local state: opening a row,
  // "Back to register", the browser back button, a refresh and a bookmark all agree
  // because the URL is the single source of truth. A ?equipment= deep-link (block
  // "Open", global search) still lands straight on that device. `initialEquipmentId`
  // is the first-paint value from the route; the live search param drives it after.
  const navigate = useNavigate();
  const liveEquipment = useRouterState({
    select: (s) => {
      const leaf = s.matches[s.matches.length - 1]?.search as Record<string, unknown> | undefined;
      return (leaf?.equipment as string | undefined) ?? undefined;
    },
  });
  const openId = liveEquipment ?? initialEquipmentId ?? null;
  const openDevice = (id: string | null): void => {
    void navigate({ search: ((prev: Record<string, unknown>) => ({ ...prev, equipment: id ?? undefined })) as never });
  };
  const [klass, setKlass] = useState("all");
  const [find, setFind] = useState("");

  const assets = useGetV1Assets({ site });
  const alerts = useGetV1Alerts({ site });

  const envelope = assets.data?.status === 200 ? assets.data.data.data : undefined;
  const rows = useMemo(
    () => ((envelope?.items ?? []) as unknown as AssetRow[]),
    [envelope],
  );
  const withheld = (envelope as unknown as { withheld?: Record<string, { template?: string }> })
    ?.withheld ?? {};

  // Real open-alert counts per device, scoped to THIS site so a device key that
  // repeats across sites cannot be credited with another site's alerts.
  const alertCount = useMemo(() => {
    const list = alerts.data?.status === 200 ? (alerts.data.data.data?.alerts ?? []) : [];
    const m = new Map<string, number>();
    for (const a of list) {
      if (!a.active || !a.device) continue;
      m.set(a.device, (m.get(a.device) ?? 0) + (a.count ?? 1));
    }
    return m;
  }, [alerts.data]);
  const alertsKnown = alerts.data?.status === 200;

  const classes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const c = r.device_class ?? "unclassified";
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  if (openId) {
    return <EquipmentPage site={site} equipmentId={openId} onBack={() => openDevice(null)} />;
  }

  if (assets.isLoading) {
    return (
      <div className="content" aria-busy="true">
        <Skeleton variant="text" width="14rem" />
        <Skeleton variant="row" lines={8} />
      </div>
    );
  }
  if (assets.isError || !assets.data || assets.data.status !== 200) {
    return (
      <ErrorState
        title="Couldn't load the equipment register"
        message="The device registry for this site could not be read."
        onRetry={() => void assets.refetch()}
      />
    );
  }

  const needle = find.trim().toLowerCase();
  const visible = rows.filter((r) => {
    const classOk = klass === "all" || (r.device_class ?? "unclassified") === klass;
    if (!classOk) return false;
    if (!needle) return true;
    return [r.role_name, r.id, r.make, r.model, r.serial]
      .some((v) => typeof v === "string" && v.toLowerCase().includes(needle));
  });

  const blockReason = withheld["block_id"]?.template;
  const showBlock = !blockReason && rows.some((r) => r.block_id);

  // Registry truth: a device with no telemetry key is fitted-but-not-instrumented.
  // Served additively, so when it is absent the tiles withhold instead of guessing.
  const monitoringKnown = rows.some((r) => typeof r.monitored === "boolean");
  const instrumented = rows.filter((r) => r.monitored === true).length;
  const registryOnly = rows.filter((r) => r.monitored === false).length;

  const warrantyKnown = rows.some((r) => typeof r.warranty_to === "string" && r.warranty_to);
  const today = new Date().toISOString().slice(0, 10);
  const warrantyActive = rows.filter(
    (r) => typeof r.warranty_to === "string" && r.warranty_to >= today,
  ).length;

  const withAlerts = new Set(
    rows.filter((r) => r.id && (alertCount.get(r.id) ?? 0) > 0).map((r) => r.id),
  ).size;

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <PageHeader
        title={`Site ${site} · Equipment`}
        scope={`From the registry · ${rows.length} ${rows.length === 1 ? "device" : "devices"} · the whole register, in one read`}
        showTimeControl={false}
      />
      <SiteTabBar site={site} />

      {/* ── Tiles: only what the register can actually state ── */}
      <div className="sk-tiles-6">
        <EquipTile
          label="Equipment"
          value={String(rows.length)}
          unit="items"
          sub={`${classes.length} ${classes.length === 1 ? "class" : "classes"} recorded`}
        />
        {monitoringKnown ? (
          <>
            <EquipTile
              label="Instrumented"
              value={String(instrumented)}
              sub="a telemetry key is assigned"
            />
            <EquipTile
              label="Registry only"
              value={String(registryOnly)}
              sub="fitted, not instrumented · no telemetry"
            />
          </>
        ) : (
          <>
            <WithheldTile
              label="Instrumented"
              why="the registry read does not state whether a device is instrumented yet"
            />
            <WithheldTile
              label="Registry only"
              why="the registry read does not state whether a device is instrumented yet"
            />
          </>
        )}
        <WithheldTile
          label="Fully monitored"
          why="'every expected signal reporting' needs the live-store join this screen has no endpoint for"
        />
        {alertsKnown ? (
          <EquipTile
            label="With open alerts"
            value={String(withAlerts)}
            sub={`${[...alertCount.values()].reduce((a, b) => a + b, 0)} open alerts at this site`}
          />
        ) : (
          <WithheldTile label="With open alerts" why="the alerts read for this site failed" />
        )}
        {warrantyKnown ? (
          <EquipTile
            label="Under warranty"
            value={String(warrantyActive)}
            sub={`of ${rows.filter((r) => r.warranty_to).length} with a recorded warranty date`}
          />
        ) : (
          <WithheldTile
            label="Under warranty"
            why="no warranty end date is authored against the devices at this site"
          />
        )}
      </div>

      {/* ── Class chips: a real GROUP BY; a class the site lacks is absent ── */}
      <div className="sk-chip-row" role="group" aria-label="Equipment class">
        <button
          type="button"
          className={`chip clickable ${klass === "all" ? "on" : ""}`}
          onClick={() => setKlass("all")}
          aria-pressed={klass === "all"}
        >
          All {rows.length}
        </button>
        {classes.map(([c, n]) => (
          <button
            key={c}
            type="button"
            className={`chip clickable ${klass === c ? "on" : ""}`}
            onClick={() => setKlass(c)}
            aria-pressed={klass === c}
          >
            {classLabel(c)} {n}
          </button>
        ))}
        <input
          className="sk-toolbar__find"
          style={{ marginLeft: "auto" }}
          placeholder="Find name, id, make, model"
          aria-label="Find equipment"
          value={find}
          onChange={(e) => setFind(e.target.value)}
        />
      </div>

      {/* ── Register table ── */}
      {visible.length === 0 ? (
        <EmptyState
          variant="no_data"
          title={rows.length === 0 ? "No devices recorded for this site" : "No device matches this filter"}
        />
      ) : (
        <table className="table" aria-label="Equipment register">
          <thead>
            <tr>
              <th>Equipment</th>
              <th>Class</th>
              <th className="num">Rating</th>
              <th>Make and model</th>
              {showBlock ? <th>Block</th> : null}
              <th>Monitoring</th>
              <th className="num">Open alerts</th>
              <th>Commissioned</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => {
              const rating = ratingOf(r);
              const makeModel = [r.make, r.model].filter(Boolean).join(" · ");
              const n = r.id ? alertCount.get(r.id) : undefined;
              return (
                <tr key={r.id ?? i}>
                  <td style={{ fontWeight: 600 }}>{r.role_name ?? r.id ?? <NotRecorded what="role name" />}</td>
                  <td>{r.device_class ? classLabel(r.device_class) : <NotRecorded what="device class" />}</td>
                  <td className="num">{rating ?? <NotRecorded what="nameplate rating" />}</td>
                  <td>{makeModel || <NotRecorded what="make and model" />}</td>
                  {showBlock ? <td>{r.block_id ?? <NotRecorded what="block" />}</td> : null}
                  <td>
                    {typeof r.monitored === "boolean" ? (
                      r.monitored ? "instrumented" : (
                        <span className="text-text-muted">registry only</span>
                      )
                    ) : (
                      <NotRecorded what="monitoring state" />
                    )}
                  </td>
                  <td className="num">
                    {alertsKnown ? (n ?? 0) : <NotRecorded what="open alerts" />}
                  </td>
                  <td>{fmtDate(r.effective_from ?? undefined) ?? <NotRecorded what="commissioning date" />}</td>
                  <td style={{ textAlign: "right" }}>
                    {typeof r.id === "string" ? (
                      <button type="button" className="btn small" onClick={() => openDevice(String(r.id))}>Open</button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="text-text-muted" style={{ fontSize: "0.72rem", lineHeight: 1.6 }}>
        <p style={{ margin: 0 }}>
          Every row opens that device&apos;s equipment page by its registry key. The
          whole register arrives in one read — there is no paging to scroll.
        </p>
        <p style={{ margin: "4px 0 0" }}>
          Columns not shown, and why:{" "}
          {[
            ...WITHHELD_COLUMNS.map((c) => `${c.column} — ${c.why}`),
            blockReason
              ? `Block — ${REASON_TEXT[blockReason] ?? blockReason.toLowerCase().replace(/_/g, " ")}`
              : null,
            warrantyKnown ? null : "Warranty to — no warranty end date is authored for these devices",
          ]
            .filter(Boolean)
            .join(" · ")}
          .
        </p>
      </div>
    </div>
  );
}

function EquipTile({ label, value, unit, sub }: {
  label: string; value: string; unit?: string; sub: string;
}): ReactNode {
  return (
    <div className="sk-tile">
      <span className="sk-tile__label">{label}</span>
      <span className="sk-metric__value">
        {value}{unit ? <span className="sk-metric__unit">{unit}</span> : null}
      </span>
      <span className="sk-tile__sub">{sub}</span>
    </div>
  );
}

/** A tile with no number behind it: the state word and the reason, never a 0. */
function WithheldTile({ label, why }: { label: string; why: string }): ReactNode {
  return (
    <div className="sk-tile">
      <span className="sk-tile__label">{label}</span>
      <span className="sk-metric__value">
        <span className="sk-stateword" title={why}>Not measured</span>
      </span>
      <span className="sk-tile__sub">{why}</span>
    </div>
  );
}

/** A cell with no recorded value: the state word, never a bare dash or a zero. */
function NotRecorded({ what }: { what: string }): ReactNode {
  return (
    <span className="sk-stateword" title={`Not recorded in the registry: ${what}.`}>
      Not recorded
    </span>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   §09 — Equipment page. The same page serves every class. Reads the real
   /v1/equipment/{id}; opened from a register row or a deep link.
   ════════════════════════════════════════════════════════════════════════════ */

function EquipmentPage({ site, equipmentId, onBack }: {
  site: string; equipmentId: string; onBack: () => void;
}): ReactNode {
  const q = useGetV1EquipmentId(equipmentId);
  // "Explorer" opens this site's meters-and-sensors explorer (wireframe §09
  // annotation 4). Derived from the current path so it carries the tenant and
  // scope without re-deriving them.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const explorerHref = pathname.replace(/\/equipment\/?$/, "/explorer");
  // The header time control (Today/7D/30D) is URL state; it sets the window the
  // 30-day history card reads. Default 30d, as the wireframe shows active.
  const period = useRouterState({
    select: (s) => {
      const leaf = s.matches[s.matches.length - 1]?.search as Record<string, unknown> | undefined;
      return (leaf?.period as string | undefined) ?? "30d";
    },
  });

  if (q.isLoading) {
    return (
      <div className="content" aria-busy="true">
        <Skeleton variant="text" width="12rem" />
        <Skeleton variant="row" lines={8} />
      </div>
    );
  }
  if (q.isError || !q.data || q.data.status !== 200) {
    return <ErrorState title="Couldn't load equipment" onRetry={() => void q.refetch()} />;
  }

  const data = q.data.data.data;
  const np = data?.nameplate;
  const ext = (np ?? {}) as Record<string, unknown>;
  const npWithheld = (ext["withheld"] ?? {}) as Record<string, { template?: string }>;
  const signals = data?.signals ?? [];
  const expected = signals.filter((s) => s.expected).length;
  const reporting = signals.filter((s) => s.expected && s.reporting).length;
  const top = (data ?? {}) as Record<string, unknown>;
  const signalsState = top["signals_state"] as { reason?: { template?: string } } | null | undefined;
  const h = data?.history_30d;
  const hCounts = ((h ?? {}) as Record<string, unknown>)["counts"] as
    Record<string, Figure> | undefined;
  const actions = data?.allowed_actions ?? [];
  const findings = data?.findings ?? [];
  const findingsCount = top["findings_count"] as Figure | undefined;

  const label = data?.role_name ?? data?.id ?? equipmentId;
  const devClass = data?.device_class ?? "";

  const acCapKw = typeof np?.capacity_ac?.value === "number" ? np.capacity_ac.value : null;
  const dcCapKw = typeof np?.capacity_dc?.value === "number" ? np.capacity_dc.value : null;
  const firmware = typeof ext["firmware"] === "string" ? (ext["firmware"] as string) : null;
  const warranty = typeof ext["warranty_to"] === "string" ? (ext["warranty_to"] as string) : null;
  const warrantyClaim =
    typeof ext["warranty_claim"] === "string" ? (ext["warranty_claim"] as string) : null;
  const oem = typeof ext["oem"] === "string" ? (ext["oem"] as string) : null;
  const position = typeof ext["position"] === "string" ? (ext["position"] as string) : null;
  const blockId = np?.block_id ?? null;

  const subtitleParts = [
    blockId ? `Block ${blockId}` : null,
    devClass ? devClass.replace(/_/g, " ") : null,
    acCapKw ? `${acCapKw.toLocaleString()} kW` : null,
  ].filter(Boolean);

  const findingAwait = findings.find((f) => f.lane === "awaiting_approval");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Header ── matches the pagehead grammar (§09): title at the title
            token, meta in ink-2, on one baseline. The sizes are the design
            tokens, not rem (rem resolved against a 17px root, which ran the
            title and meta ~6% large with a loose line height). */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", padding: "14px 0 6px" }}>
        <button type="button" className="btn small" onClick={onBack} style={{ alignSelf: "center" }}>← Back to register</button>
        <h2 style={{ margin: 0, fontSize: "var(--sk-type-title-size)", fontWeight: 600, lineHeight: 1.25 }}>{label}</h2>
        <span style={{ fontSize: 12, color: "var(--sk-color-ink-2)" }}>{subtitleParts.join(" · ")}</span>
        {findingAwait && (
          <span className="sk-basis-chip">
            Awaiting approval · {findingAwait.id ?? ""}
          </span>
        )}
        {/* Right cluster: the Today/7D/30D control (drives the history window
            below) and the wireframe's primary action. The button is disabled
            until the action resolver and work-order flow are wired (same honest
            pattern as the block page) — shown where it belongs, never faking. */}
        <div style={{ marginLeft: "auto", alignSelf: "center", display: "flex", alignItems: "center", gap: 8 }}>
          <TimeControl visiblePeriods={["today", "7d", "30d"]} />
          <button
            type="button"
            className="btn primary"
            disabled
            title="Raising a work order isn't wired yet — the action resolver and work-order flow are not connected."
          >
            Raise work order
          </button>
        </div>
      </div>

      {/* ── Row 1: three cards ── */}
      <div className="sk-eq-row">
        {/* Identity and nameplate */}
        <div className="sk-eq-card">
          <h4 className="sk-eq-card__title">Identity and nameplate<span className="sk-eq-card__from">from the registry</span></h4>
          <div style={npGridStyle}>
            <NpRow
              label="Make and model"
              value={[np?.make, np?.model].filter(Boolean).join(" · ")}
              withheld={npWithheld["make"] ?? npWithheld["model"]}
              what="make and model"
            />
            <NpRow label="Serial" value={np?.serial ?? ""} withheld={npWithheld["serial"]} what="serial number" />
            <NpRow
              label="Rating"
              value={[acCapKw ? `${acCapKw.toLocaleString()} kW AC` : null, dcCapKw ? `${dcCapKw.toLocaleString()} kWp DC` : null].filter(Boolean).join(" · ")}
              what="nameplate rating"
            />
            <NpRow
              label="Firmware"
              value={firmware ?? ""}
              withheld={npWithheld["firmware"]}
              what="firmware version"
            />
            <NpRow label="Commissioned" value={fmtDate(np?.effective_from ?? undefined) ?? ""} what="commissioning date" />
            <NpRow
              label="Warranty"
              value={
                warranty
                  ? `to ${fmtDate(warranty) ?? warranty}${warrantyClaim ? ` · claim ${warrantyClaim}` : ""}${oem ? ` · ${oem}` : ""}`
                  : ""
              }
              withheld={npWithheld["warranty_to"]}
              what="warranty end date"
            />
            <NpRow
              label="Position"
              value={position ?? ""}
              withheld={npWithheld["position"]}
              what="position on the single-line diagram"
            />
            <NpRow
              label="Expected signals"
              value={signalsState ? "" : `${reporting} of ${expected} reporting`}
              withheld={signalsState?.reason}
              what="signal coverage"
            />
          </div>
        </div>

        {/* Health now */}
        <HealthNowCard data={top} />

        {/* Allowed here */}
        <div className="sk-eq-card">
          <h4 className="sk-eq-card__title">Allowed here<span className="sk-eq-card__from">by reach, authority and tier</span></h4>
          {actions.length === 0 ? (
            <p className="muted" style={{ fontSize: "0.78rem", margin: 0 }}>
              <span className="sk-stateword" title="Per-device actions are not available yet.">
                Not measured
              </span>{" "}
              — the actions available on this device are not resolved yet, so none are
              listed. An empty list here is not &ldquo;no permissions&rdquo;.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {actions.map((a, i) => (
                <div key={a.action ?? i} className="sk-eq-action">
                  <span className="sk-eq-action__name">{String(a.action ?? "action").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}</span>
                  <span className="sk-eq-action__meta">{[a.reach ? String(a.reach).replace(/_/g, " ") : null, a.authority ? String(a.authority).replace(/_/g, " ") : null].filter(Boolean).join(" · ")}</span>
                </div>
              ))}
            </div>
          )}
          {/* The Easy-M tier note (wireframe §09 annotation 3): the list shows
              acknowledge; control actions appear locked with the tier and reach
              that would provide them. */}
          <div className="sk-lockbox" style={{ marginTop: 8, gap: 10 }}>
            <b>Easy M</b> shows acknowledge only; control actions appear locked with the tier and reach that provide them
          </div>
        </div>
      </div>

      {/* ── Row 2: history + alerts/docs ── */}
      <div className="sk-eq-row sk-eq-row--2col">
        <div className="sk-eq-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <h4 className="sk-eq-card__title" style={{ margin: 0 }}>
              {period === "today" ? "Today" : period === "7d" ? "7-day history" : "30-day history"}
            </h4>
            <span className="text-text-muted" style={{ fontSize: "0.68rem" }}>
              this device&apos;s AC power, {period === "today" ? "hourly" : "daily"}
            </span>
            <Link to={explorerHref as never} className="btn small" style={{ marginLeft: "auto" }}>
              Explorer
            </Link>
          </div>

          <HistoryChart site={site} device={data?.id ?? equipmentId} period={period} />

          <div className="sk-eq-timeline">
            {findings.length > 0 ? (
              findings.slice().sort((a, b) => (b.opened_at ?? "").localeCompare(a.opened_at ?? "")).map((f, i) => {
                const cls = f.class ?? "";
                const icon = cls === "fault" || cls === "safety" ? "■" : cls === "performance" || cls === "curtailment" ? "▲" : "●";
                const iconMod = cls === "fault" || cls === "safety" ? " sk-eq-timeline__icon--fault" : cls === "performance" || cls === "curtailment" ? " sk-eq-timeline__icon--replace" : " sk-eq-timeline__icon--maint";
                const detail = [f.title, f.what].filter(Boolean).join(" · ");
                const tail = [f.id ?? null, f.lane ? String(f.lane).replace(/_/g, " ") : null].filter(Boolean).join(" · ");
                return (
                  <div key={f.id ?? i} className="sk-eq-timeline__row">
                    <span className="sk-eq-timeline__date">{fmtEvent(f.opened_at) ?? ""}</span>
                    <span className={`sk-eq-timeline__icon${iconMod}`}>{icon}</span>
                    <span>{detail || "Finding"}{tail ? <span className="text-text-muted"> · {tail}</span> : null}</span>
                  </div>
                );
              })
            ) : (
              // "No findings" would assert a check that was never run.
              <p className="muted" style={{ fontSize: "0.78rem" }}>
                Findings:{" "}
                {findingsCount ? (
                  <StateWord state={findingsCount.state} reason={findingsCount.reason} />
                ) : (
                  <span className="sk-stateword">Not measured</span>
                )}{" "}
                — no findings store is wired, so this device has not been assessed.
              </p>
            )}
            {hCounts ? (
              <div className="sk-eq-timeline__row" style={{ marginTop: 4, color: "var(--sk-text-muted)" }}>
                <span className="sk-eq-timeline__date">30 days</span>
                <span className="sk-eq-timeline__icon">·</span>
                <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 10 }}>
                  {/* UX-REV-002 M2: the raw fault-register row count (tens of
                      thousands of change-logged bit rows) is not a fault tally and
                      read as "61,786 faults". The decoded, still-raised faults are
                      the verdict view in the Alerts card; this raw count stays behind
                      the Device alarms door, not shouted on the 30-day line. */}
                  <CountChip label="performance events" figure={hCounts["performance_events"]} />
                  <CountChip label="maintenance" figure={hCounts["maintenance_events"]} />
                  <CountChip label="replacements" figure={hCounts["replacements"]} />
                  {h?.warranty_state ? <span>warranty {h.warranty_state}</span> : null}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <EquipmentAlertsCard site={site} equipmentId={data?.id ?? equipmentId} />
          <div className="sk-eq-card">
            <h4 className="sk-eq-card__title">Documents and SOPs</h4>
            <p className="muted" style={{ fontSize: "0.78rem", margin: 0 }}>
              <span className="sk-stateword" title="No document store is connected to the platform.">
                Not measured
              </span>{" "}
              — manuals, SOPs and commissioning records are held outside the platform
              and no document store is connected, so none can be listed here.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** One 30-day count, as a figure — withheld shows the state word, never a 0. */
function CountChip({ label, figure }: { label: string; figure?: Figure }): ReactNode {
  if (!figure) return null;
  return (
    <span>
      <Metric figure={figure} maximumFractionDigits={0} showFreshness={false} /> {label}
    </span>
  );
}

function HealthNowCard({ data }: { data: Record<string, unknown> }): ReactNode {
  const health = (data["health"] ?? {}) as { state?: string | null; reason?: { template?: string } };
  const freshness = data["freshness"] as Record<string, unknown> | undefined;
  const powerFig = data["power"] as Figure | undefined;
  const energyFig = data["energy"] as Figure | undefined;
  const dcFig = data["dc"] as Figure | undefined;

  return (
    <div className="sk-eq-card">
      <h4 className="sk-eq-card__title">Health now</h4>
      {/* Wireframe §09 head: the status marker + a prominent state line, then
          the modifiers/freshness line beneath (muted). We carry the state and
          freshness; reach/authority modifiers aren't served, so they're absent,
          not faked. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {health.state ? (
            <HealthBadge state={health.state as HealthState} showLabel />
          ) : (
            // A device whose live path cannot be read is NOT "unmonitored" —
            // defaulting to a state here would invent a verdict.
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: "var(--sk-color-ink-3)", display: "inline-block" }} />
              <span className="sk-stateword" title="The live path for this device could not be read.">
                Cannot check
              </span>
            </span>
          )}
        </div>
        {typeof freshness?.["age_s"] === "number" ? (
          <div className="text-text-muted" style={{ fontSize: "0.72rem" }}>
            fresh {fmtAge(freshness["age_s"] as number)}
          </div>
        ) : null}
      </div>
      <div className="sk-panel-card__tiles">
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">AC power</span>
          <span className="sk-panel-tile__value">
            {/* A withheld figure is NEVER zero (ruling §3.2). */}
            {powerFig ? (
              <Metric figure={powerFig} showProvenance showFreshness={false} />
            ) : (
              <span className="sk-stateword" title="The platform did not state a power figure for this device.">
                Cannot check
              </span>
            )}
          </span>
        </div>
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">Today</span>
          <span className="sk-panel-tile__value">
            {energyFig ? (
              <Metric figure={energyFig} showFreshness={false} />
            ) : (
              <span className="sk-stateword">Not measured</span>
            )}
          </span>
        </div>
        {/* The third of the three figures a person checks first (wireframe §09
            annotation 2). The class sets which one it is — DC/MPPT for an
            inverter — so the figure is read straight from the payload and shows
            its own state; a withheld figure is never a 0. */}
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">DC</span>
          <span className="sk-panel-tile__value">
            {dcFig ? (
              <Metric figure={dcFig} showFreshness={false} />
            ) : (
              <span className="sk-stateword" title="The platform did not state a DC figure for this device.">
                Not measured
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

function windowFor(period: string): { fromIso: string; grain: string } {
  const d = new Date();
  if (period === "today") {
    d.setHours(0, 0, 0, 0);
    return { fromIso: d.toISOString(), grain: "1h" };
  }
  d.setDate(d.getDate() - (period === "7d" ? 7 : 30));
  return { fromIso: d.toISOString(), grain: "1d" };
}

function HistoryChart({ site, device, period }: { site: string; device: string; period: string }): ReactNode {
  // Both ends of the window are memoised — windowFor() calls new Date(), so a
  // fresh `from`/`to` each render would change the query key and loop the fetch
  // forever (Sudesh; SensorView.tsx pattern). `from` depends on the period.
  const { fromIso, grain } = useMemo(() => windowFor(period), [period]);
  const now = useMemo(() => new Date().toISOString(), []);
  // The real per-device series: this site, this device, a signal that exists.
  // (It used to pass the equipment id as the SITE and ask for a metric the
  // platform does not have, so it could never render.) The window follows the
  // header's Today/7D/30D control.
  const q = useGetV1Series({
    site,
    device,
    signal: "ac_active_power",
    from: fromIso,
    to: now,
    grain,
  });
  const ok = q.data?.status === 200 ? q.data.data.data : undefined;
  const pts = ok?.points ?? [];
  if (q.isLoading) return <div style={{ height: 160, display: "grid", placeItems: "center", color: "var(--sk-text-muted)", fontSize: 11 }}>Loading…</div>;
  if (q.isError || (q.data && q.data.status !== 200)) {
    return <div style={{ height: 160, display: "grid", placeItems: "center", color: "var(--sk-text-muted)", fontSize: 11 }}>Series unavailable for this device</div>;
  }
  if (pts.length === 0) return <div style={{ height: 160, display: "grid", placeItems: "center", color: "var(--sk-text-muted)", fontSize: 11 }}>Nothing reported for this device in the last 30 days</div>;
  return <TimeSeriesTile label="AC power" unit="kW" times={pts.map((p) => Date.parse(p.ts) / 1000)} values={pts.map((p) => p.value ?? null)} height={160} />;
}

function EquipmentAlertsCard({ site, equipmentId }: { site: string; equipmentId: string }): ReactNode {
  // Scoped to this site: a device key that repeats across sites must not pick up
  // another site's alerts.
  const q = useGetV1Alerts({ site });
  const allAlerts = q.data?.status === 200 ? (q.data.data.data?.alerts ?? []) : [];
  const alerts = allAlerts.filter((a) => a.device === equipmentId);
  return (
    <div className="sk-eq-card">
      <h4 className="sk-eq-card__title">Alerts<span className="sk-eq-card__from">this device · 30 days</span></h4>
      {q.isLoading ? (
        <Skeleton variant="row" lines={3} />
      ) : q.isError || (q.data && q.data.status !== 200) ? (
        <p className="muted" style={{ fontSize: "0.78rem", margin: 0 }}>
          <span className="sk-stateword">Cannot check</span> — the alerts read failed.
        </p>
      ) : alerts.length === 0 ? (
        <EmptyState variant="verified_quiet" title="No alerts for this device" />
      ) : (
        <table className="table" aria-label="Equipment alerts" style={{ fontSize: "0.78rem" }}>
          {/* UX-REV-002 M2 (UX-002 P6 two-door rule): the engine's VERDICT leads —
              the decoded fault name ("Grid Overvoltage"), not the raw register
              word ("fault_status_word_4"). The panel code (UF201) an engineer
              reads at the device sits in Code; the raw register word stays behind
              the Device alarms door, not shouted in this primary summary. */}
          <thead>
            <tr><th>Alert</th><th>Code</th><th className="num">Count</th><th>Last</th><th>State</th></tr>
          </thead>
          <tbody>
            {alerts.map((a, i) => (
              <tr key={`${String(a.code ?? a.label ?? "")}-${i}`}>
                <td>{a.label ?? a.fault_level_label ?? a.code ?? <NotRecorded what="alert" />}</td>
                <td className="code">{a.display_code ?? <NotRecorded what="panel code" />}</td>
                <td className="num">{a.count ?? 1}</td>
                <td>{a.last_seen ? fmtTimeShort(String(a.last_seen)) : <NotRecorded what="last seen" />}</td>
                <td>{a.active
                  ? <span style={stateChipStyle}>Active</span>
                  : <span style={clearedChipStyle}>Cleared</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// The nameplate grammar (wireframe §09 `.nameplate`): a 2-column grid of
// field blocks, each a block label over a MONOSPACE value. Built inline because
// the shared styles.css (which carries the old 4-column `.sk-xf-nameplate`) is
// not ours to change.
const npGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px 14px",
  fontSize: 11,
  padding: "8px 0",
};
const npLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 9.5,
  textTransform: "uppercase",
  letterSpacing: ".05em",
  color: "var(--sk-color-ink-3)",
  fontWeight: 600,
  marginBottom: 1,
};
const npValStyle: React.CSSProperties = {
  fontFamily: "var(--sk-font-mono)",
  fontSize: 11,
  color: "var(--sk-color-ink-1)",
};

// The alert-table STATE chip is the wireframe's `.chip.state`: a neutral
// OUTLINED chip, never a filled status colour (the status is the word, not a
// red fill). Cleared is the same chip, muted.
const stateChipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 18,
  padding: "0 6px",
  borderRadius: "var(--sk-radius-chip, 999px)",
  fontSize: 10,
  fontWeight: 500,
  border: "1px solid var(--sk-color-ink-2)",
  color: "var(--sk-color-ink-1)",
  background: "var(--sk-color-surface-0)",
  whiteSpace: "nowrap",
};
const clearedChipStyle: React.CSSProperties = {
  ...stateChipStyle,
  border: "1px solid var(--sk-color-border-2)",
  color: "var(--sk-color-ink-3)",
};

function NpRow({ label, value, withheld, what }: {
  label: string;
  value: string;
  withheld?: { template?: string };
  what: string;
}): ReactNode {
  return (
    <div>
      <span style={npLabelStyle}>{label}</span>
      <span style={npValStyle}>
        {value ? value : <NotRecorded what={withheld?.template ? reasonWords(withheld.template) : what} />}
      </span>
    </div>
  );
}

/* ── Helpers ── */
function reasonWords(template: string): string {
  return template.toLowerCase().replace(/^not_measured_/, "").replace(/_/g, " ");
}
function fmtAge(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  return `${Math.floor(seconds / 3600)} h`;
}
function fmtDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function fmtEvent(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  if (d.getHours() === 0 && d.getMinutes() === 0) return day;
  return `${day} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
}
function fmtTimeShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}
